import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Save,
  Loader2,
  User
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const AgencyInfoSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [agencyInfo, setAgencyInfo] = useState({
    name: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    website: '',
    account_manager: {
      name: '',
      email: '',
      phone: ''
    }
  });

  useEffect(() => {
    fetchAgencyInfo();
  }, []);

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo({
        name: res.data.name || '',
        address: res.data.address || '',
        postal_code: res.data.postal_code || '',
        city: res.data.city || '',
        country: res.data.country || 'France',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        account_manager: res.data.account_manager || { name: '', email: '', phone: '' }
      });
    } catch (error) {
      console.error('Error fetching agency info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/agency-info', agencyInfo);
      toast.success('Informations de l\'agence mises à jour !');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setAgencyInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateAccountManager = (field, value) => {
    setAgencyInfo(prev => ({
      ...prev,
      account_manager: { ...prev.account_manager, [field]: value }
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Informations de l'agence
            </h1>
            <p className="text-[#64748b] mt-1">
              Ces informations sont affichées dans le header et sur le site vitrine
            </p>
          </div>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            style={{ backgroundColor: theme.colors?.accent }}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informations générales */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Building2 className="w-5 h-5" />
                  Informations générales
                </CardTitle>
                <CardDescription>Nom et coordonnées de l'agence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de l'agence</Label>
                  <Input
                    value={agencyInfo.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="MSC L'AGENCE"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Téléphone
                  </Label>
                  <Input
                    value={agencyInfo.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+33 1 23 45 67 89"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={agencyInfo.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="contact@agence.fr"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Site web
                  </Label>
                  <Input
                    value={agencyInfo.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="www.agence.fr"
                    className="input-default"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Adresse */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <MapPin className="w-5 h-5" />
                  Adresse
                </CardTitle>
                <CardDescription>Localisation de l'agence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={agencyInfo.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="12 Rue de l'Agence"
                    className="input-default"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      value={agencyInfo.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="75001"
                      className="input-default"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={agencyInfo.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Paris"
                      className="input-default"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input
                    value={agencyInfo.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    placeholder="France"
                    className="input-default"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Responsable de compte */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <User className="w-5 h-5" />
                  Responsable de compte par défaut
                </CardTitle>
                <CardDescription>
                  Contact affiché pour les nouveaux clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Nom</Label>
                    <Input
                      value={agencyInfo.account_manager?.name || ''}
                      onChange={(e) => updateAccountManager('name', e.target.value)}
                      placeholder="Jean Dupont"
                      className="input-default"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={agencyInfo.account_manager?.email || ''}
                      onChange={(e) => updateAccountManager('email', e.target.value)}
                      placeholder="jean@agence.fr"
                      className="input-default"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      value={agencyInfo.account_manager?.phone || ''}
                      onChange={(e) => updateAccountManager('phone', e.target.value)}
                      placeholder="+33 6 12 34 56 78"
                      className="input-default"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgencyInfoSettingsPage;
